/** An activity's opening hours as a week (plan §3 "Opening hours widget", D5): one row
 *  per weekday, Monday first, each **Closed**, **Open** with one or more `HH:MM–HH:MM`
 *  periods (24-hour; a close before the open is the next day), or **Open 24 h**. A plain
 *  controlled component over the stored `opening_hours` list; the record editor's
 *  `opening_hours` widget wraps it (the registry, `index.ts`).
 *
 *  - `value` is the stored list as the API returns it (read leniently, `parseOpeningHours`;
 *    what the week cannot show exactly is an info note); `onChange` gets the canonical
 *    list (`toOpeningPeriods`). While the operator edits, the widget keeps its own week and
 *    takes `value` again only when it is not what it last sent (an undo, a reload) — so a
 *    period typed as 00:00–23:59 does not turn into "Open 24 h" under their hands.
 *  - Times are the record editor's time field (`HH:mm`, sectioned): a half-typed time is
 *    reported through `onInvalid` and never sent. A week that is well-formed but breaks a
 *    rule (a period that opens and closes at the same time, overlapping periods) is sent
 *    and shown on its day; the caller blocks its save with the same `openingHoursError`.
 *  - Switching a day to "Open" copies the nearest earlier day with hours (else
 *    09:00–17:00), so a week takes one choice per day.
 *  - **Copy to all days** (each row's copy button): the other six days take that day's
 *    hours — Closed and Open 24 h included — in ONE change sent as its own undo step
 *    (`onChange(…, { label, ownStep })`), never folded into the focus session's typing.
 *    It is off while the day holds a half-typed time, or when every day already agrees.
 *  - Every row reserves the input height in both modes (the Zero-Re-Layout Rule):
 *    `readOnly` is view mode, the same seven rows in words (a closed day muted), and the
 *    copy button's column is kept empty there. A week with no hours keeps its seven rows
 *    and says "the app shows none" in one caption slot under them, in both modes — so E
 *    moves nothing. */
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { LocalizationProvider, TimeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import {
  addPeriod,
  copyDayToAll,
  DAY_KIND_LABEL,
  dayProblems,
  formatDayHours,
  formatPeriod,
  isClosedWeek,
  isSameEveryDay,
  parseOpeningHours,
  removePeriod,
  setDayKind,
  setPeriodTime,
  toOpeningPeriods,
  WEEK_ORDER,
  WEEKDAY_NAME,
  WEEKDAY_SHORT,
  type DayKind,
  type OpeningPeriod,
  type OpenPeriod,
  type Weekday,
  type WeekHours,
} from './openingHoursModel';
import { pickerToWire, wireToPicker } from './TemporalWidgets';
import { WidgetCaption } from './WidgetCaption';

export interface OpeningHoursProps {
  /** The stored `opening_hours`, as the API returns it (any shape; read leniently). */
  value: unknown;
  /** The canonical list. `opts` comes with a structural edit (Copy to all days): its
   *  history label, and `ownStep` — ONE undo step of its own, not part of the focus
   *  session's. */
  onChange: (value: OpeningPeriod[], opts?: OpeningHoursChangeOptions) => void;
  /** A time the value cannot hold yet (the record editor's `onInvalid`); null when none. */
  onInvalid?: (message: string | null) => void;
  /** Focus enters / leaves the whole week (one undo step per focus session). */
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  /** View mode: each day in words. */
  readOnly?: boolean;
  /** An error from outside (the server's), shown under the week. */
  error?: string | null;
  /** The group's accessible name when no `labelledBy` is given (default "Opening hours"). */
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  id?: string;
}

export interface OpeningHoursChangeOptions {
  label: string;
  ownStep: true;
}

export const INCOMPLETE_TIME = 'Enter a complete time (HH:MM, 24-hour)';

/** The copy action's words: its tooltip, and (with the day) its name and history label. */
export const COPY_TO_ALL_DAYS = 'Copy to all days';
export const copyToAllDaysLabel = (dayName: string) => `Copy ${dayName} to all days`;
const SAME_EVERY_DAY = 'Every day already has these hours';

/** A week with no hours, under its seven rows in both modes. */
export const NO_HOURS = 'No opening hours: the app shows none.';

const DAY_KINDS: readonly DayKind[] = ['closed', 'hours', 'open24'];

const ROW_PX = 40;
/** The copy button's column, kept in both modes. */
const COPY_PX = 32;

/** What the widget holds beside its props: the week being edited, the list it last sent
 *  (to tell an echo of its own change from a change made elsewhere), and the half-typed
 *  times by `day:index:side`. */
interface Local {
  value: unknown;
  week: WeekHours;
  sent: OpeningPeriod[] | null;
  incomplete: Readonly<Record<string, true>>;
}

const sameList = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

const keyOf = (d: Weekday, i: number, side: keyof OpenPeriod) => `${d}:${i}:${side}`;

const dropDay = (incomplete: Local['incomplete'], d: Weekday) =>
  Object.fromEntries(Object.entries(incomplete).filter(([k]) => !k.startsWith(`${d}:`)));

/** One time of one period: the record editor's `HH:mm` field. Its Date is memoised per
 *  wire value, so a re-render never resets what the operator is half-way through typing. */
function PeriodTime({
  time,
  label,
  error,
  disabled,
  describedBy,
  focusKey,
  onTime,
  onIncomplete,
}: {
  time: string;
  label: string;
  error: boolean;
  disabled: boolean;
  describedBy: string | undefined;
  focusKey: string;
  onTime: (time: string) => void;
  onIncomplete: (incomplete: boolean) => void;
}) {
  const date = useMemo(() => wireToPicker('time', time), [time]);
  return (
    <TimeField
      value={date}
      onChange={(d: Date | null) => {
        if (d === null || Number.isNaN(d.getTime())) {
          onIncomplete(true);
          return;
        }
        onIncomplete(false);
        const next = pickerToWire('time', d);
        if (next !== time) onTime(next);
      }}
      format="HH:mm"
      ampm={false}
      size="small"
      disabled={disabled}
      // Undefined keeps the field's own validation; true adds the day's rule problems.
      slotProps={{ textField: { error: error || undefined } }}
      inputProps={{
        'aria-label': label,
        'aria-describedby': describedBy,
        'data-focus': focusKey,
      }}
      sx={{ width: 84, '& input': { fontVariantNumeric: 'tabular-nums' } }}
    />
  );
}

export function OpeningHours({
  value,
  onChange,
  onInvalid,
  onFocus,
  onBlur,
  disabled = false,
  readOnly = false,
  error,
  label = 'Opening hours',
  labelledBy,
  describedBy,
  id,
}: OpeningHoursProps) {
  const uid = useId();
  const [local, setLocal] = useState<Local>(() => ({
    value,
    week: parseOpeningHours(value).week,
    sent: null,
    incomplete: {},
  }));
  // Adopt a new `value` during render — unless it is the echo of our own last change.
  let state = local;
  if (local.value !== value) {
    const echo = local.sent !== null && sameList(value, local.sent);
    state = echo
      ? { ...local, value }
      : { value, week: parseOpeningHours(value).week, sent: null, incomplete: {} };
    setLocal(state);
  }
  const { week, incomplete } = state;
  const notes = useMemo(() => parseOpeningHours(value).notes, [value]);

  const firstIncomplete = WEEK_ORDER.find((d) =>
    Object.keys(incomplete).some((k) => k.startsWith(`${d}:`)),
  );
  const message =
    firstIncomplete === undefined
      ? null
      : `${WEEKDAY_NAME[firstIncomplete]}: ${INCOMPLETE_TIME.charAt(0).toLowerCase()}${INCOMPLETE_TIME.slice(1)}`;
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (reported.current === message) return;
    reported.current = message;
    onInvalid?.(message);
  }, [message, onInvalid]);

  // Where focus goes after a row changes shape (a period added or removed).
  const rows = useRef<Partial<Record<Weekday, HTMLLIElement | null>>>({});
  const pendingFocus = useRef<{ day: Weekday; key: string } | null>(null);
  useEffect(() => {
    const p = pendingFocus.current;
    if (!p) return;
    pendingFocus.current = null;
    rows.current[p.day]?.querySelector<HTMLElement>(`[data-focus="${p.key}"]`)?.focus();
  });

  /** Send a new week. Functional updates, so a time that has just stopped being
   *  half-typed (`onIncomplete(false)`, the same event) is not put back. `clearDays`: the
   *  days whose half-typed times the edit replaces. */
  const emit = (next: WeekHours, clearDays: readonly Weekday[] = [], opts?: OpeningHoursChangeOptions) => {
    const sent = toOpeningPeriods(next);
    setLocal((s) => ({
      value,
      week: next,
      sent,
      incomplete: clearDays.reduce((acc, d) => dropDay(acc, d), s.incomplete),
    }));
    if (opts) onChange(sent, opts);
    else onChange(sent);
  };

  const groupName = labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': label };
  const leaves = (e: FocusEvent<HTMLElement>) =>
    !e.currentTarget.contains(e.relatedTarget as Node | null);
  const closedWeek = isClosedWeek(week);

  const dayRow = (d: Weekday, index: number) => {
    const day = week[d];
    const name = WEEKDAY_NAME[d];
    const problems = readOnly ? [] : dayProblems(day);
    const dayIncomplete = Object.keys(incomplete).some((k) => k.startsWith(`${d}:`));
    const captions = [...(dayIncomplete ? [INCOMPLETE_TIME] : []), ...problems];
    const captionId = `${uid}-day-${d}`;
    const described =
      [captions.length ? captionId : null, describedBy].filter(Boolean).join(' ') || undefined;

    let control: ReactNode;
    if (readOnly) {
      control = (
        <Typography
          variant="body2"
          color={day.kind === 'closed' ? 'text.secondary' : undefined}
          sx={{ lineHeight: `${ROW_PX}px`, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatDayHours(day)}
        </Typography>
      );
    } else {
      const periods = day.kind === 'hours' ? day.periods : [];
      const prefix = (i: number) => (periods.length > 1 ? `${name} period ${i + 1}` : name);
      const time = (p: OpenPeriod, i: number, side: keyof OpenPeriod) => (
        <PeriodTime
          time={p[side]}
          label={`${prefix(i)} ${side === 'open' ? 'opens' : 'closes'}`}
          error={problems.length > 0}
          disabled={disabled}
          describedBy={described}
          focusKey={`${side}-${i}`}
          onTime={(t) => emit(setPeriodTime(week, d, i, side, t))}
          onIncomplete={(on) => {
            const k = keyOf(d, i, side);
            setLocal((s) => {
              if (!!s.incomplete[k] === on) return s;
              const next = { ...s.incomplete };
              if (on) next[k] = true;
              else delete next[k];
              return { ...s, incomplete: next };
            });
          }}
        />
      );
      control = (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <TextField
            select
            size="small"
            value={day.kind}
            disabled={disabled}
            onChange={(e) => emit(setDayKind(week, d, e.target.value as DayKind), [d])}
            SelectProps={{
              inputProps: { 'aria-label': `${name} hours`, 'aria-describedby': described },
              SelectDisplayProps: { 'data-focus': 'kind' } as HTMLAttributes<HTMLDivElement>,
            }}
            sx={{ width: 136 }}
          >
            {DAY_KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {DAY_KIND_LABEL[k]}
              </MenuItem>
            ))}
          </TextField>
          {periods.map((p, i) => (
            <Box
              key={i}
              role="group"
              aria-label={prefix(i)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {time(p, i, 'open')}
              <Typography aria-hidden variant="body2" color="text.secondary">
                –
              </Typography>
              {time(p, i, 'close')}
              <IconButton
                size="small"
                disabled={disabled}
                aria-label={`Remove ${name} ${formatPeriod(p)}`}
                onClick={() => {
                  const next = removePeriod(week, d, i);
                  pendingFocus.current = { day: d, key: next[d].kind === 'hours' ? 'add' : 'kind' };
                  emit(next, [d]);
                }}
              >
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {day.kind === 'hours' && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              aria-label={`Add hours to ${name}`}
              data-focus="add"
              onClick={() => {
                pendingFocus.current = { day: d, key: `open-${periods.length}` };
                emit(addPeriod(week, d));
              }}
            >
              Add hours
            </Button>
          )}
        </Box>
      );
    }

    // Copy to all days: its own column, kept empty in view mode (E moves nothing).
    let copy: ReactNode = null;
    if (!readOnly) {
      const same = isSameEveryDay(week, d);
      const off = disabled || dayIncomplete || same;
      const button = (
        <IconButton
          size="small"
          disabled={off}
          aria-label={copyToAllDaysLabel(name)}
          data-copy-day={d}
          onClick={() =>
            emit(
              copyDayToAll(week, d),
              WEEK_ORDER.filter((o) => o !== d),
              { label: copyToAllDaysLabel(name), ownStep: true },
            )
          }
        >
          <ContentCopyIcon sx={{ fontSize: 18 }} />
        </IconButton>
      );
      copy = (
        // The button's name says it all; the tooltip is the sighted hint (describeChild: it
        // names nothing). A disabled button gets no pointer events: the span carries it.
        <Tooltip title={same && !disabled ? SAME_EVERY_DAY : COPY_TO_ALL_DAYS} describeChild>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {button}
          </Box>
        </Tooltip>
      );
    }

    return (
      <Box
        component="li"
        key={d}
        ref={(el: HTMLLIElement | null) => {
          rows.current[d] = el;
        }}
        data-day={d}
        sx={{
          display: 'grid',
          gridTemplateColumns: `40px minmax(0, 1fr) ${COPY_PX}px`,
          columnGap: 1.5,
          alignItems: 'start',
          minHeight: ROW_PX,
          py: 0.5,
          borderTop: index ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: `${ROW_PX}px` }}>
          <Box component="abbr" title={name} sx={{ textDecoration: 'none' }}>
            {WEEKDAY_SHORT[d]}
          </Box>
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          {control}
          {captions.length > 0 && (
            <Box id={captionId} sx={{ mt: 0.5 }}>
              {captions.map((c) => (
                <WidgetCaption key={c} tone="error">
                  {c}
                </WidgetCaption>
              ))}
            </Box>
          )}
        </Box>
        <Box data-part="copy" sx={{ height: ROW_PX, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {copy}
        </Box>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        role="group"
        id={id}
        {...groupName}
        onFocus={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFocus?.();
        }}
        onBlur={(e) => {
          if (leaves(e)) onBlur?.();
        }}
      >
        {notes.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            {notes.map((n) => (
              <WidgetCaption key={n} tone="info">
                {n}
              </WidgetCaption>
            ))}
          </Box>
        )}
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {WEEK_ORDER.map(dayRow)}
        </Box>
        {closedWeek && <WidgetCaption tone="info">{NO_HOURS}</WidgetCaption>}
        {error && <WidgetCaption tone="error">{error}</WidgetCaption>}
      </Box>
    </LocalizationProvider>
  );
}
